import moment from 'moment-timezone';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import { FilterPlacement, PageFilters } from '../../../core/components/PageFilters';
import UserGroupPermissions, {
  viewPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { useAppContext } from '../../../providers/AppProvider';
import { selectSite } from '../../../store/authReducer';
import { selectMultiSiteSelection } from '../../site-selection/reducer';
import { fetchHourlyWashCounts } from '../../wash-counts/actions';
import { selectHourlyWashCounts } from '../../wash-counts/reducer';
import { setFromDate, setUntilDate } from '../actions';
import ClaimsKpiDataGetter from '../components/ClaimsKpiDataGetters';
import CostPerCarDataGetter from '../components/CostPerCarDataGetter';
import GuestCancellationDataGetter from '../components/GuestCancellationDataGetter';
import InstaClubDataGetter from '../components/InstaClubDataGetter';
import RevenueComparisonKpiDataGetter from '../components/RevenueComparisonKpiDataGetter';
import { selectFromDate, selectUntilDate } from '../reducer';
import { useIntl } from 'react-intl';
import DetergentsKpiChartDataGetter from '../components/DetergentsKpiDataGetter';
import OccupancyKpi from '../components/OccupancyKpi';
import { selectSiteSettings } from '../../settings/reducer';
import { getDetergentPods } from '../actions';
import { WashCountTable } from '../components/WashCountTable';
import { DailyWashCountGraph } from '../components/DailyWashCountGraph';
import { CostPerCarArcs } from '../components/CostPerCarArcs';
import { CashierTrackingGraph } from '../components/CashierTrackingGraph';
import { GuestRatingKpi } from '../components/GuestRatingKpi';
import { useFeatureFlag } from '../../../core/hooks/useFeatureFlag';

export const OVERVIEW_YELLOW = 'rgb(254, 206, 0)';

const toFilterDate = (value: moment.MomentInput): Date | null => {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

export const MyCharts = (): JSX.Element => {
  const chartCashierTracking = useFeatureFlag('chart-cashier-tracking', true);
  const chartCostPerCar = useFeatureFlag('chart-cost-per-car', true);
  const chartGuestRating = useFeatureFlag('chart-guest-rating', true);
  const dispatch = useDispatch(),
    intl = useIntl(),
    timeZone = useAppContext().timeZone,
    fromDate = useSelector(selectFromDate),
    untilDate = useSelector(selectUntilDate),
    multiSiteSelection = useSelector(selectMultiSiteSelection),
    hourlyWashCounts = useSelector(selectHourlyWashCounts),
    siteSettings = useSelector(selectSiteSettings),
    primarySiteId = useSelector(selectSite);

  const getHourlyWashCounts = () => {
    const from = moment.tz(fromDate, timeZone);
    const until = moment.tz(untilDate, timeZone);
    const fromISO = from.toISOString();
    const untilISO = from.isSame(until, 'day') ? fromISO : until.toISOString();
    dispatch(fetchHourlyWashCounts(fromISO, untilISO));
  };
  const fetchDetergentPods = () => {
    dispatch(getDetergentPods());
  };
  useEffect(() => {
    fetchDetergentPods();
  }, [primarySiteId, multiSiteSelection]);
  useEffect(() => {
    getHourlyWashCounts();
  }, [multiSiteSelection, fromDate, untilDate, timeZone]);

  const filterConfigs = useMemo(
    () => [
      {
        id: 'dateRange',
        type: 'dateRange' as const,
        name: intl.formatMessage({ id: 'date' }),
        placement: FilterPlacement.Chiclet,
        defaultVisible: true,
        icon: <i className="uil uil-calendar-alt" />,
        startDate: toFilterDate(fromDate),
        endDate: toFilterDate(untilDate),
        native: true,
        onChange: ([startDate, endDate]: [Date | null, Date | null]) => {
          dispatch(setFromDate(startDate ? moment(startDate) : fromDate));
          dispatch(setUntilDate(endDate ? moment(endDate) : untilDate));
        },
      },
    ],
    [dispatch, fromDate, intl, untilDate]
  );

  return (
    <Container fluid className="overview">
      <div className="date-time-overview filters-actions-container no-padding">
        <PageFilters filters={filterConfigs} maxChicletFilters={1} />
      </div>
      <div className="three-column-grid">
        <div>
          <UserGroupPermissions itemRef={viewPermissionRefs.hourly_comparision_chart}>
            <WashCountTable hourlyCounts={hourlyWashCounts} />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.wash_count_chart}>
            <DailyWashCountGraph />
          </UserGroupPermissions>
          {chartCashierTracking && (
            <UserGroupPermissions itemRef={viewPermissionRefs.cashier_tracking}>
              <CashierTrackingGraph />
            </UserGroupPermissions>
          )}
          <UserGroupPermissions itemRef={viewPermissionRefs.survey_chart}>
            <GuestCancellationDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
        </div>
        <div>
          <UserGroupPermissions itemRef={viewPermissionRefs.window_sales}>
            <InstaClubDataGetter fromDate={fromDate} untilDate={untilDate} timeZone={timeZone} />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.detergent_wip}>
            <DetergentsKpiChartDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
          {chartGuestRating && <GuestRatingKpi />}
        </div>
        <div>
          {chartCostPerCar && (
            <UserGroupPermissions itemRef={viewPermissionRefs.cost_per_car}>
              <CostPerCarArcs />
            </UserGroupPermissions>
          )}
          <UserGroupPermissions itemRef={viewPermissionRefs.occupancy_chart}>
            <OccupancyKpi
              hourlyWashCounts={hourlyWashCounts}
              fromDate={fromDate}
              untilDate={untilDate}
              tunnelLength={siteSettings?.tunnelLength}
            />
          </UserGroupPermissions>
          <UserGroupPermissions itemRef={viewPermissionRefs.claims_chart}>
            <ClaimsKpiDataGetter fromDate={fromDate} untilDate={untilDate} />
          </UserGroupPermissions>
        </div>
      </div>
    </Container>
  );
};
export default MyCharts;
